/**
 * Widget Type Definitions
 */

export type LongChatPosition =
    | 'bottom-right'
    | 'bottom-left'
    | 'top-right'
    | 'top-left';

/**
 * Options for initializing the LongChat widget
 */
export interface LongChatInitOptions {
  /** Account ID in LongChat */
  accountId: string;
  /** Agent ID to chat with */
  agentId: string;

  fullscreen?: boolean;

  /** Widget language */
  lang?: 'he' | 'en';
  /** Theme customization */
  theme?: {
    /** Primary color for buttons and user messages */
    primaryColor?: string;
    /** Widget launcher position */
    position?: LongChatPosition;
    /** Floating launcher diameter in pixels. Safe range: 40-96 */
    buttonSize?: number;
    /** Distance from the selected horizontal edge in pixels. Safe range: 0-200 */
    offsetX?: number;
    /** Distance from the selected vertical edge in pixels. Safe range: 0-200 */
    offsetY?: number;
  };
  /** Pre-fill user information */
  user?: {
    /** New preferred field: how the bot should address the user */
    firstName?: string;

    /** New preferred field: any contact information string */
    contactInformation?: string;

    /** Optional stable external identifier */
    externalId?: string;
  };

  /** Initial context to pass to the chat */
  context?: Record<string, any> & {
    /** New preferred field: free context for who the bot is talking to */
    userContext?: string;
  };
  /** Firebase configuration (optional - uses env vars if not provided) */
  firebaseConfig?: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
  };
}

/**
 * Agent information for widget display
 */
export interface AgentInfo {
  name: string;
  role?: string;
  avatarIndex?: number;
  /** Optional avatar image URL. Falls back to an icon based on avatarIndex when absent. */
  avatarUrl?: string;
}

/**
 * Global LongChat API exposed on window
 */
export interface LongChatGlobal {
  /** Initialize the widget */
  init(options: LongChatInitOptions): void;
  /** Open the chat window */
  open(): void;
  /** Close the chat window */
  close(): void;
  /** Send a message programmatically */
  sendMessage(text: string): Promise<void>;
  /** Destroy the widget and clean up */
  destroy(): void;
  /** Check if widget is initialized */
  isInitialized(): boolean;
}

/**
 * Internal widget state
 */
export interface WidgetState {
  isOpen: boolean;
  isInitialized: boolean;
  chatId: string | null;
  visitorId: string;
  messages: WidgetMessage[];
  isLoading: boolean;
  error: string | null;
  agentInfo: AgentInfo | null;
}

/**
 * Message type for widget display
 */
export interface WidgetMessage {
  id: string;
  senderType: 'endUser' | 'bot' | 'agent' | 'system';
  content: string;
  timestamp: Date;
  status: 'pending' | 'sent' | 'failed';
  senderName?: string;
}

/**
 * Hebrew labels for the widget
 */
export const HEBREW_LABELS = {
  fabTooltip: 'יש לכם שאלה? דברו איתנו',
  chatHeader: 'איך נוכל לעזור?',
  inputPlaceholder: 'הקלידו הודעה...',
  sendButton: 'שלח',
  emptyState: 'שלחו לנו הודעה ונחזור אליכם בהקדם',
  errorState: 'אירעה שגיאה, נסו שוב',
  connecting: 'מתחבר...',
  typingIndicator: 'מקליד...',
  menu: 'תפריט',
  menuNewChat: 'שיחה חדשה',
  menuClearChat: 'נקה שיחה מהמסך',
  expand: 'הגדלה',
  collapse: 'הקטנה',
  newChatConfirmTitle: 'להתחיל שיחה חדשה?',
  newChatConfirmAgent: 'ייתכן שנציג אנושי מחובר כעת לשיחה. פתיחת שיחה חדשה תסיים את השיחה הנוכחית.',
  confirm: 'התחל שיחה חדשה',
  cancel: 'ביטול',
};

/**
 * English labels for the widget
 */
export const ENGLISH_LABELS = {
  fabTooltip: 'Have a question? Chat with us',
  chatHeader: 'How can we help?',
  inputPlaceholder: 'Type a message...',
  sendButton: 'Send',
  emptyState: 'Send us a message and we\'ll get back to you soon',
  errorState: 'An error occurred, please try again',
  connecting: 'Connecting...',
  typingIndicator: 'Typing...',
  menu: 'Menu',
  menuNewChat: 'Start new chat',
  menuClearChat: 'Clear chat from screen',
  expand: 'Expand',
  collapse: 'Collapse',
  newChatConfirmTitle: 'Start a new chat?',
  newChatConfirmAgent: 'A live agent may currently be connected. Starting a new chat will end the current conversation.',
  confirm: 'Start new chat',
  cancel: 'Cancel',
};

export type Labels = typeof HEBREW_LABELS;

/**
 * Get labels for a language
 */
export function getLabels(lang: 'he' | 'en'): Labels {
  return lang === 'he' ? HEBREW_LABELS : ENGLISH_LABELS;
}

// Extend Window interface
declare global {
  interface Window {
    LongChat: LongChatGlobal;
  }
}
